import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Alert, Badge, Spinner } from 'react-bootstrap';
import moment from 'moment';
import { shiftAPI } from '../../services';
import { getErrorMessage } from '../../utils/apiClient';

/**
 * #015110 — sửa TAY một ca ngay trên bảng ca đề xuất dạng lịch tuần (AC2).
 *
 * Khác `EditApprovedShiftModal` (chỉ đổi mẫu ca + cờ QC của ca ĐÃ DUYỆT): ở đây việc chính là
 * ĐỔI NGƯỜI, và danh sách người chọn được lấy từ `GET /shifts/available-employees` — chính bộ
 * lọc mà hệ thống đang dùng để biết ai đủ điều kiện vào ca đó (cùng chi nhánh, không kẹt ca
 * khác, có đăng ký ca ưu tiên hay không). Không tự dựng danh sách nhân viên riêng ở đây, nếu
 * không màn này sẽ cho chọn cả người mà backend chắc chắn từ chối.
 *
 * Ca sửa xong VẪN là đề xuất chưa duyệt — backend không đụng status/approved_by/approved_at.
 *
 * #001848 — CHỈ hiện người CÓ ĐĂNG KÝ vào đúng ca đó. `GET /shifts/available-employees` trả về
 * TOÀN BỘ nhân viên đang hoạt động của chi nhánh (chỉ gắn cờ, không lọc), nên trước đây đổi người
 * đề xuất là xổ ra cả danh sách nhân viên. Cờ `has_preference` của chính API đó = "có đăng ký ca
 * ưu tiên cho đúng mẫu ca + đúng thứ này" — lọc theo nó, không tự dựng bộ lọc riêng.
 *
 * `proposedCountByEmployee` (#001848): số ca ĐÃ ĐƯỢC ĐỀ XUẤT trong tuần đang xem, để người duyệt
 * thấy ngay ai đang bị dồn bao nhiêu ca trước khi đổi.
 */
export default function EditProposedShiftModal({
  show,
  onHide,
  shift,
  templates = [],
  onSaved,
  proposedCountByEmployee = {},
}) {
  const [employeeId, setEmployeeId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show || !shift) return;
    setEmployeeId(shift.employee_id || '');
    setTemplateId(shift.template_id || '');
    setError('');

    // Không có mẫu ca thì không có bộ lọc "ai vào được ca này" — để trống, người dùng vẫn đổi
    // được mẫu ca rồi mở lại.
    if (!shift.template_id) {
      setCandidates([]);
      return;
    }

    let cancelled = false;
    setLoadingCandidates(true);
    shiftAPI
      .getAvailableEmployees(shift.template_id, shift.start_time, shift.end_time)
      .then((res) => {
        if (cancelled) return;
        const list = res?.available_employees || res?.data?.available_employees || [];
        // #001848 — chỉ giữ người CÓ ĐĂNG KÝ vào ca này (xem ghi chú đầu file).
        setCandidates(list.filter((c) => c.has_preference));
      })
      .catch((err) => {
        if (cancelled) return;
        setCandidates([]);
        setError(getErrorMessage(err) || 'Không tải được danh sách nhân viên có thể vào ca này');
      })
      .finally(() => {
        if (!cancelled) setLoadingCandidates(false);
      });

    return () => {
      cancelled = true;
    };
  }, [show, shift]);

  const handleSave = async () => {
    if (!shift) return;
    const payload = {};
    if (employeeId && employeeId !== shift.employee_id) payload.employee_id = employeeId;
    if (templateId && templateId !== shift.template_id) payload.template_id = templateId;

    if (Object.keys(payload).length === 0) {
      onHide();
      return;
    }

    try {
      setSaving(true);
      setError('');
      const updated = await shiftAPI.updateShift(shift.id, payload);
      onSaved?.(updated?.data || updated);
    } catch (err) {
      // Báo NGUYÊN câu backend nói (trùng giờ, khác chi nhánh, mẫu ca không mở ngày đó...) —
      // đây đúng là thứ quản lý cần để biết phải sửa gì, đừng nuốt thành "lỗi không xác định".
      setError(getErrorMessage(err) || 'Lưu thay đổi thất bại');
    } finally {
      setSaving(false);
    }
  };

  const currentEmployeeInList = candidates.some((c) => c.id === shift?.employee_id);

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="md"
    >
      <Modal.Header closeButton>
        <Modal.Title>Sửa ca đề xuất</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!shift ? (
          <div className="text-muted">Không có dữ liệu</div>
        ) : (
          <>
            <div className="mb-3">
              <Badge bg="warning" text="dark">🤖 Đề xuất — chưa duyệt</Badge>{' '}
              <span className="text-muted small">
                {moment(shift.start_time).format('dddd DD/MM/YYYY HH:mm')} –{' '}
                {moment(shift.end_time).format('HH:mm')}
              </span>
            </div>

            {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}

            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Nhân viên</Form.Label>
                {loadingCandidates ? (
                  <div className="text-muted small">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Đang tải danh sách nhân viên...
                  </div>
                ) : (
                  <Form.Select
                    data-testid="proposed-shift-employee"
                    value={employeeId}
                    onChange={(e) => setEmployeeId(e.target.value)}
                  >
                    {!currentEmployeeInList && (
                      <option value={shift.employee_id || ''}>
                        {shift.employee_name || 'Nhân viên hiện tại'} (đang xếp)
                      </option>
                    )}
                    {candidates.map((c) => (
                      <option
                        key={c.id}
                        value={c.id}
                        disabled={!c.is_available}
                      >
                        {c.full_name}
                        {` — đã đề xuất ${proposedCountByEmployee[c.id] || 0} ca`}
                        {c.is_preferred ? ' ⭐ ca ưu tiên' : ''}
                        {c.is_available ? '' : ' — đang kẹt ca khác'}
                      </option>
                    ))}
                  </Form.Select>
                )}
                <Form.Text className="text-muted">
                  Chỉ hiện người <strong>có đăng ký vào đúng ca này</strong> (đúng mẫu ca, đúng thứ trong
                  tuần), kèm số ca đã được đề xuất trong tuần đang xem.
                </Form.Text>
                {!loadingCandidates && candidates.length === 0 && (
                  <div
                    className="small text-danger mt-1"
                    data-testid="proposed-shift-no-registrant"
                  >
                    Chưa có nhân viên nào đăng ký ca này.
                  </div>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Mẫu ca (đổi mẫu sẽ đổi luôn khung giờ)</Form.Label>
                <Form.Select
                  data-testid="proposed-shift-template"
                  value={templateId}
                  onChange={(e) => setTemplateId(e.target.value)}
                >
                  <option value={shift.template_id || ''}>
                    {shift.template_name || 'Giữ nguyên mẫu ca hiện tại'}
                  </option>
                  {templates
                    .filter((t) => t.id !== shift.template_id)
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  Giờ ca được tính lại theo mẫu, vẫn giữ đúng NGÀY của ca.
                </Form.Text>
              </Form.Group>
            </Form>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>
          Hủy
        </Button>
        <Button
          data-testid="proposed-shift-save"
          variant="primary"
          onClick={handleSave}
          disabled={saving || !shift}
        >
          {saving ? 'Đang lưu...' : 'Lưu'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
